import { Button } from 'designSystem/components/Button'
import { Checkbox } from 'designSystem/components/Checkbox'
import { KNOB_HELP } from 'features/timeSeries/experiment/knobCopy'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import {
  SplitChunk,
  SplitRole,
  SplitSpec,
  SplitUnit,
} from 'features/timeSeries/experiment/types'
import { FC } from 'react'
import styled from 'styled-components'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Label = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
`

const Select = styled.select`
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
`

const Input = styled.input`
  width: 4.5rem;
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
`

const Timeline = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`

const Chunk = styled.button<{ $role: string }>`
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 4px;
  padding: 0.35rem 0.45rem;
  font-size: 0.7rem;
  cursor: pointer;
  min-width: 3.5rem;
  background: ${({ $role, theme }) =>
    $role === 'train'
      ? 'rgba(34, 211, 238, 0.18)'
      : $role === 'val'
        ? 'rgba(249, 115, 22, 0.2)'
        : $role === 'test'
          ? 'rgba(239, 68, 68, 0.2)'
          : theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
`

const Legend = styled.div`
  display: flex;
  gap: 0.75rem;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const CheckRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  cursor: pointer;
`

const nextRole = (role: string): SplitRole => {
  if (role === 'train') return 'val'
  if (role === 'val') return 'test'
  return 'train'
}

/** Units whose chunks the backend can tail-split (chunk_tail_frac). */
const TAIL_UNITS: readonly SplitUnit[] = ['month', 'year', 'days']

export interface SplitBuilderProps {
  split: SplitSpec
  chunks: SplitChunk[]
  onChange: (split: SplitSpec) => void
}

export const SplitBuilder: FC<SplitBuilderProps> = ({
  split,
  chunks,
  onChange,
}) => {
  const tailOn = typeof split.chunk_tail_frac === 'number'

  const setUnit = (unit: SplitUnit) => {
    // Chunk ids change with the unit, so assignments are stale; the tail
    // fraction only means something for month/year/days chunking.
    const next: SplitSpec = { ...split, unit, assignments: {} }
    if (!TAIL_UNITS.includes(unit)) delete next.chunk_tail_frac
    onChange(next)
  }

  const cycleChunk = (id: string, current: string) => {
    const assignments = { ...(split.assignments || {}) }
    if (tailOn && current === 'test') {
      // In tail mode the cycle ends by clearing the explicit role, handing
      // the chunk back to the per-chunk tail split (role becomes "mixed").
      delete assignments[id]
      onChange({ ...split, assignments })
      return
    }
    assignments[id] = nextRole(current)
    onChange({ ...split, assignments })
  }

  const setTailEnabled = (on: boolean) => {
    const next: SplitSpec = { ...split }
    if (on) {
      next.chunk_tail_frac = split.chunk_tail_frac ?? 0.1
    } else {
      delete next.chunk_tail_frac
    }
    onChange(next)
  }

  const setTailPct = (pct: number) => {
    if (!Number.isFinite(pct)) return
    // Backend rejects values outside (0, 1); clamp the percent input.
    const frac = Math.min(Math.max(pct / 100, 0.01), 0.99)
    onChange({ ...split, chunk_tail_frac: frac })
  }

  const applyPreset = (kind: 'tail' | 'alt_months' | 'year_holdout') => {
    if (kind === 'tail') {
      onChange({
        unit: 'tail_frac',
        train_frac: 0.9,
        assignments: { head: 'train', tail: 'test' },
      })
      return
    }
    if (kind === 'alt_months') {
      const assignments: Record<string, string> = {}
      chunks.forEach((c, i) => {
        assignments[c.id] = i % 3 === 2 ? 'test' : i % 3 === 1 ? 'val' : 'train'
      })
      onChange({ unit: 'month', assignments })
      return
    }
    // year_holdout: last year = test, previous = val, rest train
    const assignments: Record<string, string> = {}
    chunks.forEach((c, i) => {
      if (i === chunks.length - 1) assignments[c.id] = 'test'
      else if (i === chunks.length - 2) assignments[c.id] = 'val'
      else assignments[c.id] = 'train'
    })
    onChange({ unit: 'year', assignments })
  }

  return (
    <Wrap>
      <Label>
        Train / val / test — click a chunk to cycle roles. Train fits; val
        calibrates the band; test is held out for metrics.
      </Label>
      <Row>
        <KnobHelp label='Chunk unit' help={KNOB_HELP.splitUnit} />
        <Select
          value={split.unit}
          onChange={(e) => setUnit(e.target.value as SplitUnit)}>
          <option value='tail_frac'>Tail fraction (prod default)</option>
          <option value='month'>By month</option>
          <option value='year'>By year</option>
          <option value='days'>By N days</option>
        </Select>
        {split.unit === 'tail_frac' && (
          <>
            <KnobHelp label='train frac' help={KNOB_HELP.trainFrac} />
            <Input
              type='number'
              min={0.5}
              max={0.99}
              step={0.01}
              value={split.train_frac ?? 0.9}
              onChange={(e) =>
                onChange({
                  ...split,
                  train_frac: Number(e.target.value),
                })
              }
            />
          </>
        )}
        {split.unit === 'days' && (
          <>
            <Label>days</Label>
            <Input
              type='number'
              min={7}
              value={split.days ?? 30}
              onChange={(e) =>
                onChange({ ...split, days: Number(e.target.value) })
              }
            />
          </>
        )}
      </Row>
      {TAIL_UNITS.includes(split.unit) && (
        <Row>
          <CheckRow>
            <Checkbox
              checked={tailOn}
              onCheckedChange={(v) => setTailEnabled(!!v)}
            />
            <KnobHelp
              label='Tail-split each chunk'
              help={KNOB_HELP.chunkTailFrac}
            />
          </CheckRow>
          {tailOn && (
            <>
              <Label>last</Label>
              <Input
                type='number'
                min={1}
                max={99}
                step={1}
                value={Math.round((split.chunk_tail_frac ?? 0.1) * 100)}
                onChange={(e) => setTailPct(Number(e.target.value))}
              />
              <Label>% of each un-assigned chunk → test</Label>
            </>
          )}
        </Row>
      )}
      {tailOn && (
        <Label>
          Un-assigned chunks split in time: their last{' '}
          {Math.round((split.chunk_tail_frac ?? 0.1) * 100)}% of rows become
          test, the rest train (shown as “mixed”). Clicking a chunk still
          assigns it a whole-chunk role; click past test to hand it back to the
          tail split.
        </Label>
      )}
      <Row>
        <Button variant='outline' onClick={() => applyPreset('tail')}>
          Preset: tail 10%
        </Button>
        <Button variant='outline' onClick={() => applyPreset('alt_months')}>
          Preset: alt months
        </Button>
        <Button variant='outline' onClick={() => applyPreset('year_holdout')}>
          Preset: year holdout
        </Button>
      </Row>
      <Legend>
        <span>train (cyan)</span>
        <span>val (orange)</span>
        <span>test (red)</span>
        <span>mixed (cyan→red tail split)</span>
      </Legend>
      <Timeline>
        {chunks.map((c) => {
          const isMixed = c.role === 'mixed'
          const trainPct =
            isMixed && c.rows > 0
              ? Math.round(((c.train_rows ?? 0) / c.rows) * 100)
              : 0
          return (
            <Chunk
              key={c.id}
              type='button'
              $role={c.role}
              style={
                isMixed
                  ? {
                      background: `linear-gradient(90deg, rgba(34, 211, 238, 0.18) 0%, rgba(34, 211, 238, 0.18) ${trainPct}%, rgba(239, 68, 68, 0.2) ${trainPct}%)`,
                    }
                  : undefined
              }
              title={
                isMixed
                  ? `${c.label}: ${c.rows} rows (${c.train_rows ?? 0} train / ${c.test_rows ?? 0} test)\n${c.start ?? ''} → ${c.end ?? ''}`
                  : `${c.label}: ${c.rows} rows\n${c.start ?? ''} → ${c.end ?? ''}`
              }
              onClick={() => cycleChunk(c.id, c.role)}>
              <div>{c.label}</div>
              <div>
                {isMixed
                  ? `${c.train_rows ?? 0} train / ${c.test_rows ?? 0} test`
                  : `${c.role} · ${c.rows}`}
              </div>
            </Chunk>
          )
        })}
        {chunks.length === 0 && (
          <Label>Chunks appear after a data preview loads.</Label>
        )}
      </Timeline>
    </Wrap>
  )
}
